import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ExplanationButton from '../ExplanationButton';
import { useToast } from '../ToastSystem';
import { MaterialHistoryRowsSkeleton } from '../ContentSkeletons';
import { usePdfPreview } from '../pdf/PdfPreviewProvider';
import {
  createEventMaterial,
  createEventMaterialFolder,
  deleteEventMaterial,
  deleteEventMaterialFolder,
  getEventMaterialTree,
  listEventMaterialDownloadLogs,
  listEventMaterialVersions,
  moveEventMaterial,
  moveEventMaterialFolder,
  replaceEventMaterialVersion,
  updateEventMaterial,
  updateEventMaterialFolder,
} from '../../../api/eventDetails';
import {
  uploadMaterialThroughApi,
} from '../../../api/venues';
import {
  ConfirmMaterialDialog,
  MaterialActionMenu,
  MaterialHistoryDialog,
} from './eventMaterialExplorerParts';
import EventMaterialExplorerList from './EventMaterialExplorerList';
import {
  MAX_UPLOAD_FILES,
  formatBytes,
  getFileIcon,
  isAllowedFile,
  openOrSaveEventMaterial,
  toFolderId,
} from './eventMaterialExplorerUtils';
import '../../../css/common/events/EventMaterialExplorer.css';

const hasExternalFiles = (dataTransfer) =>
  Array.from(dataTransfer?.types || []).includes('Files');

const isDroppableTransfer = (dataTransfer, internalPayload) =>
  hasExternalFiles(dataTransfer) || !!internalPayload;

const createMaterialDragPreview = ({ count, label }) => {
  if (typeof document === 'undefined') return null;
  const element = document.createElement('div');
  element.className = `eme-drag-preview${count > 1 ? ' is-batch' : ''}`;
  const iconWrap = document.createElement('span');
  const icon = document.createElement('i');
  icon.className = 'fa-solid fa-file-lines';
  icon.setAttribute('aria-hidden', 'true');
  iconWrap.appendChild(icon);
  const text = document.createElement('strong');
  text.textContent = count > 1 ? `${count}件` : label;
  element.append(iconWrap, text);
  document.body.appendChild(element);
  window.setTimeout(() => element.remove(), 0);
  return element;
};

function EventMaterialExplorer({
  eventUuid,
  role = 'admin',
  canView = true,
  canUpload = false,
  canManage = false,
  canDownload = false,
  canViewAllLogs = false,
  title = '資料',
  showTitle = true,
  emptyLabel = '資料はまだありません',
  selectionMode = false,
  initialSelectedMaterials = /** @type {any[]} */ ([]),
  onSelectionChange,
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const pdfPreview = usePdfPreview();
  const [folderId, setFolderId] = useState(null);
  const [path, setPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragPayload, setDragPayload] = useState(null);
  const [selectedMaterialsById, setSelectedMaterialsById] = useState(() => new Map(initialSelectedMaterials
    .map((material) => [Number(material?.id || material?.event_material_id), material])
    .filter(([materialId]) => Number.isInteger(materialId) && materialId > 0)));
  const selectedMaterialIds = useMemo(() => new Set(selectedMaterialsById.keys()), [selectedMaterialsById]);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderShared, setNewFolderShared] = useState(true);
  const [rootUploadShared, setRootUploadShared] = useState(role === 'artist');
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [downloadLogModal, setDownloadLogModal] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalRows, setModalRows] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const springFolderTimerRef = useRef(null);
  const springFolderTargetRef = useRef(null);

  const isAdmin = role === 'admin';
  const visibleItemsCount = folders.length + materials.length;
  const selectedMaterialCount = selectedMaterialIds.size;
  const hasBlockingModal = Boolean(historyModal || downloadLogModal || confirmDialog);
  const currentFolderShared = useMemo(() => {
    if (!folderId) return true;
    const current = path[path.length - 1];
    return current ? current.effective_share_with_artists !== false : true;
  }, [folderId, path]);

  const loadTreeForFolder = useCallback(async (nextFolderId = null) => {
    if (!eventUuid || !canView) return;
    setLoading(true);
    try {
      const data = await getEventMaterialTree(eventUuid, nextFolderId);
      setFolderId(toFolderId(data.current_folder_id));
      setPath(Array.isArray(data.path) ? data.path : []);
      setFolders(Array.isArray(data.folders) ? data.folders : []);
      setMaterials(Array.isArray(data.materials) ? data.materials : []);
      if (!selectionMode) setSelectedMaterialsById(new Map());
    } catch (error) {
      console.error('Event materials tree fetch failed:', error);
      setFolders([]);
      setMaterials([]);
      toastError(error?.message || '資料の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [canView, eventUuid, selectionMode, toastError]);

  useEffect(() => {
    if (!selectionMode || typeof onSelectionChange !== 'function') return;
    onSelectionChange(Array.from(selectedMaterialsById.values()));
  }, [onSelectionChange, selectedMaterialsById, selectionMode]);

  const loadTree = useCallback(
    async (nextFolderId = folderId) => loadTreeForFolder(nextFolderId),
    [folderId, loadTreeForFolder]
  );

  useEffect(() => {
    loadTreeForFolder(null);
  }, [loadTreeForFolder]);

  useEffect(() => {
    if (!actionMenu) return undefined;
    const close = () => setActionMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [actionMenu]);

  const closeActionMenu = () => setActionMenu(null);

  const clearSpringFolder = useCallback(() => {
    if (springFolderTimerRef.current) {
      window.clearTimeout(springFolderTimerRef.current);
      springFolderTimerRef.current = null;
    }
    springFolderTargetRef.current = null;
  }, []);

  useEffect(() => () => clearSpringFolder(), [clearSpringFolder]);

  const scheduleSpringFolder = useCallback((nextFolderId, targetKey) => {
    if (springFolderTargetRef.current === targetKey) return;
    clearSpringFolder();
    springFolderTargetRef.current = targetKey;
    springFolderTimerRef.current = window.setTimeout(() => {
      springFolderTimerRef.current = null;
      springFolderTargetRef.current = null;
      setDragOverId(null);
      loadTree(nextFolderId);
    }, 650);
  }, [clearSpringFolder, loadTree]);

  const openActionMenu = (itemType, item, eventOrPoint) => {
    const sourceEvent = eventOrPoint?.currentTarget ? eventOrPoint : null;
    sourceEvent?.preventDefault?.();
    sourceEvent?.stopPropagation?.();
    let x = Number(eventOrPoint?.clientX);
    let y = Number(eventOrPoint?.clientY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      const rect = sourceEvent?.currentTarget?.getBoundingClientRect?.();
      x = rect ? rect.right : window.innerWidth - 24;
      y = rect ? rect.bottom + 6 : 80;
    }
    setActionMenu({
      itemType,
      item,
      x: Math.min(Math.max(12, x), window.innerWidth - 220),
      y: Math.min(Math.max(12, y), window.innerHeight - 260),
    });
  };
  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startLongPressMenu = (itemType, item, event) => {
    clearLongPressTimer();
    if (event.pointerType === 'mouse') return;
    const point = { clientX: event.clientX, clientY: event.clientY };
    longPressTimerRef.current = window.setTimeout(() => {
      suppressNextClickRef.current = true;
      openActionMenu(itemType, item, point);
    }, 520);
  };

  const runMenuAction = (handler) => {
    closeActionMenu();
    handler();
  };

  const canOpenFolderMenu = (folder) => !selectionMode && (isAdmin
    ? canManage
    : !!(folder?.can_change_share || folder?.can_rename || folder?.can_delete));

  const canOpenMaterialMenu = (material) => !selectionMode && Boolean(
    canDownload ||
    canManage ||
    (!isAdmin && (material?.can_replace || material?.can_delete || material?.can_view_download_logs))
  );

  const suppressLongPressClick = (event) => {
    if (!suppressNextClickRef.current) return false;
    suppressNextClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  };

  const toggleMaterialSelected = (material) => {
    const materialId = Number(material?.id);
    if (!Number.isInteger(materialId) || materialId <= 0) return;
    setSelectedMaterialsById((current) => {
      const next = new Map(current);
      if (next.has(materialId)) next.delete(materialId);
      else next.set(materialId, material);
      return next;
    });
  };

  const startMaterialDrag = (event, material) => {
    const ids = selectedMaterialIds.has(material.id) ? Array.from(selectedMaterialIds) : [material.id];
    setDragPayload({ type: 'materials', ids });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ids.join(','));
    const preview = createMaterialDragPreview({
      count: ids.length,
      label: material.title || material.file_name || '資料',
    });
    if (preview) event.dataTransfer.setDragImage(preview, 18, 18);
  };

  const startFolderDrag = (event, folder) => {
    setDragPayload({ type: 'folder', id: folder.id });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(folder.id));
    const preview = createMaterialDragPreview({ count: 1, label: folder.name || 'フォルダ' });
    if (preview) event.dataTransfer.setDragImage(preview, 18, 18);
  };

  const validateFiles = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return [];
    if (list.length > MAX_UPLOAD_FILES) {
      toastError(`一度にアップロードできる資料は${MAX_UPLOAD_FILES}件までです`);
      return [];
    }
    const invalid = list.find((file) => !isAllowedFile(file));
    if (invalid) {
      toastError(`アップロードできない形式またはサイズです: ${invalid.name}`);
      return [];
    }
    return list;
  };

  const uploadFiles = async (files, targetFolderId = folderId) => {
    if (!canUpload || !eventUuid) return;
    const list = validateFiles(files);
    if (!list.length) return;
    setBusy(true);
    try {
      const uploadedMaterials = [];
      for (const file of list) {
        const upload = await uploadMaterialThroughApi({ eventUuid }, file);
        const material = await createEventMaterial(eventUuid, {
          title: file.name.replace(/\.[^.]+$/, ''),
          file_name: file.name,
          file_key: upload.key,
          file_size: upload.byteSize ?? file.size,
          folder_id: targetFolderId || null,
          share_with_artists: role === 'artist' ? true : (!targetFolderId && !!rootUploadShared),
        });
        uploadedMaterials.push(material);
      }
      if (selectionMode && uploadedMaterials.length) {
        setSelectedMaterialsById((current) => {
          const next = new Map(current);
          uploadedMaterials.forEach((material) => {
            const materialId = Number(material?.id);
            if (Number.isInteger(materialId) && materialId > 0) next.set(materialId, material);
          });
          return next;
        });
      }
      toastSuccess(list.length === 1 ? '資料を追加しました' : `${list.length}件の資料を追加しました`);
      await loadTree(targetFolderId || null);
    } catch (error) {
      console.error('Event material upload failed:', error);
      toastError(error?.message || '資料のアップロードに失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await createEventMaterialFolder(eventUuid, {
        name,
        parent_folder_id: folderId,
        share_with_artists: isAdmin ? newFolderShared : true,
      });
      setNewFolderName('');
      setCreatingFolder(false);
      await loadTree(folderId);
      toastSuccess('フォルダを作成しました');
    } catch (error) {
      console.error('Event material folder create failed:', error);
      toastError(error?.message || 'フォルダの作成に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const renameFolder = async (folder) => {
    const nextName = window.prompt('フォルダ名', folder.name || '');
    if (!nextName || nextName.trim() === folder.name) return;
    setBusy(true);
    try {
      await updateEventMaterialFolder(eventUuid, folder.id, { name: nextName.trim() });
      await loadTree(folderId);
      toastSuccess('フォルダ名を変更しました');
    } catch (error) {
      toastError(error?.message || 'フォルダ名の変更に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const runConfirmedAction = async () => {
    const action = confirmDialog?.onConfirm;
    setConfirmDialog(null);
    if (typeof action === 'function') await action();
  };

  const deleteFolder = (folder) => {
    setConfirmDialog({
      title: 'フォルダを削除',
      message: 'フォルダを削除します。中身があるフォルダは削除できません。',
      confirmLabel: '削除',
      onConfirm: async () => {
        setBusy(true);
        try {
          await deleteEventMaterialFolder(eventUuid, folder.id);
          await loadTree(folderId);
          toastSuccess('フォルダを削除しました');
        } catch (error) {
          toastError(error?.message || 'フォルダの削除に失敗しました');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const toggleFolderShare = async (folder, checked) => {
    setBusy(true);
    try {
      await updateEventMaterialFolder(eventUuid, folder.id, { share_with_artists: checked });
      await loadTree(folderId);
      toastSuccess('フォルダの共有設定を更新しました');
    } catch (error) {
      toastError(error?.message || '共有設定の更新に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const toggleMaterialShare = async (material, checked) => {
    setBusy(true);
    try {
      await updateEventMaterial(eventUuid, material.id, { share_with_artists: checked });
      await loadTree(folderId);
      toastSuccess('資料の共有設定を更新しました');
    } catch (error) {
      toastError(error?.message || '共有設定の更新に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const deleteMaterialRow = (material) => {
    setConfirmDialog({
      title: '資料を削除',
      message: '資料を削除します。よろしいですか？',
      confirmLabel: '削除',
      onConfirm: async () => {
        setBusy(true);
        try {
          await deleteEventMaterial(eventUuid, material.id);
          await loadTree(folderId);
          toastSuccess('資料を削除しました');
        } catch (error) {
          toastError(error?.message || '資料の削除に失敗しました');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const downloadMaterial = (material) => openOrSaveEventMaterial(material, {
    pdfPreview,
    onSuccess: toastSuccess,
    onError: toastError,
  });

  const replaceMaterial = async (file) => {
    if (!replaceTarget || !file) return;
    const [valid] = validateFiles([file]);
    if (!valid) return;
    setBusy(true);
    try {
      const upload = await uploadMaterialThroughApi({ eventUuid }, valid);
      await replaceEventMaterialVersion(eventUuid, replaceTarget.id, {
        file_name: valid.name,
        file_key: upload.key,
        file_size: upload.byteSize ?? valid.size,
      });
      setReplaceTarget(null);
      await loadTree(folderId);
      toastSuccess('資料を差し替えました');
    } catch (error) {
      toastError(error?.message || '資料の差し替えに失敗しました');
    } finally {
      setBusy(false);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const moveDraggedItem = async (targetFolderId) => {
    if (!dragPayload) return;
    setBusy(true);
    try {
      if (dragPayload.type === 'folder') {
        await moveEventMaterialFolder(eventUuid, dragPayload.id, targetFolderId);
      } else if (dragPayload.type === 'materials') {
        const ids = Array.from(new Set(dragPayload.ids || []));
        for (const materialId of ids) {
          await moveEventMaterial(eventUuid, materialId, targetFolderId);
        }
      } else {
        await moveEventMaterial(eventUuid, dragPayload.id, targetFolderId);
      }
      await loadTree(folderId);
      setSelectedMaterialsById(new Map());
      toastSuccess(dragPayload.type === 'materials' && dragPayload.ids?.length > 1 ? `${dragPayload.ids.length}件を移動しました` : '移動しました');
    } catch (error) {
      toastError(error?.message || '移動に失敗しました');
    } finally {
      setBusy(false);
      setDragPayload(null);
      setDragOverId(null);
    }
  };

  const openVersions = async (material) => {
    setHistoryModal(material);
    setModalRows([]);
    setModalLoading(true);
    try {
      setModalRows(await listEventMaterialVersions(eventUuid, material.id));
    } catch (error) {
      toastError(error?.message || '資料履歴の取得に失敗しました');
    } finally {
      setModalLoading(false);
    }
  };

  const openDownloadLogs = async (material) => {
    setDownloadLogModal(material);
    setModalRows([]);
    setModalLoading(true);
    try {
      setModalRows(await listEventMaterialDownloadLogs(eventUuid, material.id));
    } catch (error) {
      toastError(error?.message || 'ダウンロード履歴の取得に失敗しました');
    } finally {
      setModalLoading(false);
    }
  };

  const onDropExternal = (event, targetFolderId = folderId) => {
    if (hasBlockingModal) return;
    if (!isDroppableTransfer(event.dataTransfer, dragPayload)) return;
    event.preventDefault();
    event.stopPropagation();
    clearSpringFolder();
    setDragOverId(null);
    if (event.dataTransfer?.files?.length) {
      if (!canUpload) return;
      uploadFiles(event.dataTransfer.files, targetFolderId);
      return;
    }
    if (dragPayload) moveDraggedItem(targetFolderId);
  };

  const handleExplorerDragOver = (event, targetId = 'root', springFolderId = undefined) => {
    if (hasBlockingModal) return;
    const canAcceptExternal = canUpload && hasExternalFiles(event.dataTransfer);
    if (!canAcceptExternal && !dragPayload) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = dragPayload ? 'move' : 'copy';
    setDragOverId(targetId);
    if (springFolderId === undefined) {
      clearSpringFolder();
    } else if (!(dragPayload?.type === 'folder' && dragPayload.id === springFolderId)) {
      scheduleSpringFolder(springFolderId, targetId);
    }
  };

  if (!canView) return null;

  const closeMaterialHistoryDialog = () => {
    setHistoryModal(null);
    setDownloadLogModal(null);
  };

  const materialHistoryDialog = (
    <MaterialHistoryDialog
      historyModal={historyModal}
      downloadLogModal={downloadLogModal}
      modalLoading={modalLoading}
      modalRows={modalRows}
      loadingFallback={<MaterialHistoryRowsSkeleton rows={4} />}
      onClose={closeMaterialHistoryDialog}
    />
  );

  const actionMenuDialog = (
    <MaterialActionMenu
      actionMenu={actionMenu}
      isAdmin={isAdmin}
      canManage={canManage}
      canDownload={canDownload}
      canViewAllLogs={canViewAllLogs}
      replaceInputRef={replaceInputRef}
      runMenuAction={runMenuAction}
      toggleFolderShare={toggleFolderShare}
      renameFolder={renameFolder}
      deleteFolder={deleteFolder}
      toggleMaterialShare={toggleMaterialShare}
      downloadMaterial={downloadMaterial}
      setReplaceTarget={setReplaceTarget}
      openVersions={openVersions}
      openDownloadLogs={openDownloadLogs}
      deleteMaterialRow={deleteMaterialRow}
    />
  );

  const confirmDialogNode = (
    <ConfirmMaterialDialog
      confirmDialog={confirmDialog}
      busy={busy}
      onCancel={() => setConfirmDialog(null)}
      onConfirm={runConfirmedAction}
    />
  );

  return (
    <section
      className={`event-material-explorer ${dragOverId === 'root' ? 'is-drag-over' : ''}`}
      onDragOver={(event) => handleExplorerDragOver(event, 'root')}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        clearSpringFolder();
        setDragOverId(null);
      }}
      onDrop={(event) => onDropExternal(event, folderId)}
    >
      <div className="eme-head">
        <div>
          {showTitle ? <h3>{title}</h3> : null}
          <div className="eme-breadcrumb" aria-label="資料フォルダ">
            <button
              type="button"
              className={dragOverId === 'breadcrumb-root' ? 'is-drop-target' : ''}
              onClick={() => loadTree(null)}
              onDragOver={(event) => handleExplorerDragOver(event, 'breadcrumb-root')}
              onDrop={(event) => onDropExternal(event, null)}
            >
              資料
            </button>
            {path.map((item, index) => (
              <React.Fragment key={item.id}>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <button
                  type="button"
                  className={dragOverId === `breadcrumb-${item.id}` ? 'is-drop-target' : ''}
                  onClick={() => loadTree(index === path.length - 1 ? item.id : item.id)}
                  onDragOver={(event) => handleExplorerDragOver(event, `breadcrumb-${item.id}`)}
                  onDrop={(event) => onDropExternal(event, item.id)}
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="eme-toolbar">
          {selectedMaterialCount > 0 ? (
            <>
              <span className="eme-selection">{selectedMaterialCount}件選択中</span>
              <button
                type="button"
                className="eme-selection-action"
                onClick={() => setSelectedMaterialsById(new Map())}
              >
                解除
              </button>
            </>
          ) : null}
          {isAdmin && !folderId ? (
            <div className="eme-auto-share">
              <button
                type="button"
                className={`eme-auto-share__toggle${rootUploadShared ? ' is-active' : ''}`}
                aria-pressed={rootUploadShared}
                onClick={() => setRootUploadShared((value) => !value)}
              >
                自動共有
              </button>
              <ExplanationButton
                title="自動共有"
                body="ONにすると、資料直下へ追加した管理者資料が最初からアーティスト共有になります。フォルダ内の資料はフォルダの共有設定が適用されます。"
              />
            </div>
          ) : null}
          {canUpload ? (
            <>
              <button
                type="button"
                className="eme-icon-button"
                title="フォルダを作成"
                onClick={() => setCreatingFolder((value) => !value)}
                disabled={busy}
              >
                <i className="fa-solid fa-folder-plus" />
              </button>
              <button
                type="button"
                className="eme-icon-button"
                title="資料をアップロード"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <i className="fa-solid fa-upload" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  uploadFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </>
          ) : null}
        </div>
      </div>

      {creatingFolder ? (
        <div className="eme-inline-create">
          <i className="fa-solid fa-folder" aria-hidden="true" />
          <input
            autoFocus
            value={newFolderName}
            placeholder="フォルダ名"
            onChange={(event) => setNewFolderName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') createFolder();
              if (event.key === 'Escape') setCreatingFolder(false);
            }}
          />
          {isAdmin ? (
            <label className="eme-check">
              <input
                type="checkbox"
                checked={newFolderShared}
                onChange={(event) => setNewFolderShared(event.target.checked)}
              />
              共有
            </label>
          ) : null}
          <button type="button" onClick={createFolder} disabled={busy || !newFolderName.trim()}>作成</button>
        </div>
      ) : null}

      <EventMaterialExplorerList
        canManage={canManage}
        canDownload={canDownload}
        canOpenFolderMenu={canOpenFolderMenu}
        canOpenMaterialMenu={canOpenMaterialMenu}
        clearLongPressTimer={clearLongPressTimer}
        clearSpringFolder={clearSpringFolder}
        currentFolderShared={currentFolderShared}
        downloadMaterial={downloadMaterial}
        dragOverId={dragOverId}
        emptyLabel={emptyLabel}
        folders={folders}
        formatBytes={formatBytes}
        getFileIcon={getFileIcon}
        handleExplorerDragOver={handleExplorerDragOver}
        loadTree={loadTree}
        loading={loading}
        materials={materials}
        onDropExternal={onDropExternal}
        openActionMenu={openActionMenu}
        selectedMaterialIds={selectedMaterialIds}
        selectionMode={selectionMode}
        setDragOverId={setDragOverId}
        setDragPayload={setDragPayload}
        startFolderDrag={startFolderDrag}
        startLongPressMenu={startLongPressMenu}
        startMaterialDrag={startMaterialDrag}
        suppressLongPressClick={suppressLongPressClick}
        toggleMaterialSelected={toggleMaterialSelected}
        visibleItemsCount={visibleItemsCount}
      />

      <input
        ref={replaceInputRef}
        type="file"
        hidden
        onChange={(event) => replaceMaterial(event.target.files?.[0] || null)}
      />

      {(canUpload || canDownload) ? <div className="eme-drop-hint">{canUpload
        ? 'ファイルをここへドロップして追加できます' : 'PDFはプレビュー、その他の資料はダウンロードします'}</div> : null}
      {materialHistoryDialog ? createPortal(materialHistoryDialog, document.body) : null}
      {actionMenuDialog ? createPortal(actionMenuDialog, document.body) : null}
      {confirmDialogNode}
      {busy ? <div className="eme-busy">処理中...</div> : null}
    </section>
  );
}

export default EventMaterialExplorer;
